import React from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import CPDProLandingPage from './CPDProLandingPage';
import PasswordResetPage from './PasswordResetPage';
import CpdProHomePage from './cpdpro/pages/CpdProHomePage';
import CpdProAbonnementenPage from './cpdpro/pages/CpdProAbonnementenPage';
import CpdProWaaromPage from './cpdpro/pages/CpdProWaaromPage';
import CpdProAccreditatiePage from './cpdpro/pages/CpdProAccreditatiePage';
import CpdProFaqPage from './cpdpro/pages/CpdProFaqPage';

// ScrollToTop component to handle automatic scrolling when route changes
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const MainLandingPage = () => {
  const { t } = useTranslation();
  return (
  <>
    {/* Header */}
    <header className="header">
      <div className="container">
        <div className="logo">
          <h2>Protagon</h2>
        </div>
        <nav className="nav">
          <a href="#story">{t('lp.nav.ourStory', 'Our Story')}</a>
          <a href="#mission">{t('lp.nav.mission', 'Mission')}</a>
          <div className="dropdown">
            <button className="dropdown-btn">{t('lp.nav.products', 'Our Products')}</button>
            <div className="dropdown-content">
              <Link to="/cpd-pro">CPD Pro</Link>
            </div>
          </div>
          <a href="#contact">{t('lp.nav.contact', 'Contact')}</a>
        </nav>
      </div>
    </header>

    {/* Hero Section */}
    <section className="hero">
      <div className="container">
        <div className="hero-content">
          <h1>{t('lp.hero.title', 'AI-Powered Innovation for Everyone')}</h1>
          <p className="hero-subtitle">
            {t('lp.hero.subtitle', "We harness the power of artificial intelligence to create tools that make a real difference. Our mission is simple: deploy AI innovations that help people achieve more.")}
          </p>
          <div className="hero-buttons">
            <Link to="/cpd-pro" className="primary-button">{t('lp.hero.ctaExplore', 'Explore Our Platform')}</Link>
            <a href="#story" className="secondary-button">{t('lp.hero.ctaStory', 'Learn Our Story')}</a>
          </div>
        </div>
        <div className="hero-image">
          <div className="ai-visual">
            <div className="ai-nodes">
              <div className="node"></div>
              <div className="node"></div>
              <div className="node"></div>
              <div className="node"></div>
              <div className="connection"></div>
              <div className="connection connection-2"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Story Section */}
    <section id="story" className="story">
      <div className="container">
        <h2>{t('lp.story.title', 'Our Story')}</h2>
        <div className="story-content">
          <div className="story-text">
            <p>{t('lp.story.p1', "Protagon was born from a simple belief: artificial intelligence should serve humanity, not the other way around. We're a team of innovators, developers, and dreamers who saw the incredible potential of AI to solve real-world problems.")}</p>
            <p>{t('lp.story.p2', "Every tool we build, every platform we deploy, is designed with one goal in mind – making people's lives better, more productive, and more fulfilling. We don't just develop AI; we craft intelligent solutions that understand human needs.")}</p>
          </div>
          <div className="story-visual">
            <div className="timeline">
              <div className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <h4>{t('lp.story.vision.title', 'Vision')}</h4>
                  <p>{t('lp.story.vision.desc', 'Identified the gap between AI potential and practical application')}</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <h4>{t('lp.story.innovation.title', 'Innovation')}</h4>
                  <p>{t('lp.story.innovation.desc', 'Developed our first AI-powered professional development platform')}</p>
                </div>
              </div>
              <div className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <h4>{t('lp.story.impact.title', 'Impact')}</h4>
                  <p>{t('lp.story.impact.desc', 'Continuously expanding our reach to help more people succeed')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Mission Section */}
    <section id="mission" className="mission">
      <div className="container">
        <div className="mission-content">
          <div className="mission-text">
            <h2>{t('lp.mission.title', 'Our Mission')}</h2>
            <p className="mission-statement">
              {t('lp.mission.statement', '"To democratize AI innovation and create intelligent tools that empower individuals and organizations to reach their full potential."')}
            </p>
            <div className="mission-points">
              <div className="mission-point">
                <div className="point-indicator">
                  <div className="indicator-dot"></div>
                </div>
                <div className="point-content">
                  <h4>{t('lp.mission.aiFirst.title', 'AI-First Approach')}</h4>
                  <p>{t('lp.mission.aiFirst.desc', 'Every solution we build leverages cutting-edge artificial intelligence to deliver superior results.')}</p>
                </div>
              </div>
              <div className="mission-point">
                <div className="point-indicator">
                  <div className="indicator-dot"></div>
                </div>
                <div className="point-content">
                  <h4>{t('lp.mission.humanCentered.title', 'Human-Centered Design')}</h4>
                  <p>{t('lp.mission.humanCentered.desc', 'Technology should adapt to people, not the other way around. We design with empathy and purpose.')}</p>
                </div>
              </div>
              <div className="mission-point">
                <div className="point-indicator">
                  <div className="indicator-dot"></div>
                </div>
                <div className="point-content">
                  <h4>{t('lp.mission.continuous.title', 'Continuous Innovation')}</h4>
                  <p>{t('lp.mission.continuous.desc', "We're constantly pushing boundaries, exploring new possibilities, and improving our solutions.")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Platform Section */}
    <section className="platform">
      <div className="container">
        <h2>{t('lp.platform.title', 'Our First Innovation')}</h2>
        <div className="platform-content">
          <div className="platform-description">
            <h3>{t('lp.platform.name', 'CPD Pro Platform')}</h3>
            <p>{t('lp.platform.p1', "Our flagship platform revolutionizes professional development through AI-powered learning experiences. We've created an intelligent system that adapts to individual learning styles, tracks progress meaningfully, and delivers personalized content that actually makes a difference.")}</p>
            <p>{t('lp.platform.p2', 'This is just the beginning. CPD Pro represents our commitment to using AI to solve real problems and help professionals advance their careers with confidence.')}</p>
            <Link to="/cpd-pro" className="platform-link">{t('lp.platform.experience', 'Experience CPD Pro →')}</Link>
          </div>
          <div className="platform-features">
            <div className="feature-item">
              <span className="feature-bullet">•</span>
              <span>{t('lp.platform.features.contentCuration', 'AI-powered content curation')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-bullet">•</span>
              <span>{t('lp.platform.features.progressTracking', 'Intelligent progress tracking')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-bullet">•</span>
              <span>{t('lp.platform.features.learningPaths', 'Personalized learning paths')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-bullet">•</span>
              <span>{t('lp.platform.features.certificationManagement', 'Automated certification management')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="cta-section">
      <div className="container">
        <h2>{t('lp.cta.title', 'Ready to Experience AI Innovation?')}</h2>
        <p>{t('lp.cta.subtitle', "Join us in shaping the future where AI serves humanity's greatest ambitions.")}</p>
        <a href="https://cpd-pro.app" className="primary-button large">{t('lp.cta.button', 'Get Started Today')}</a>
      </div>
    </section>

    {/* Footer */}
    <footer id="contact" className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Protagon</h3>
            <p>{t('lp.footer.tagline1', 'AI-Powered Innovation for Everyone')}</p>
            <p className="footer-tagline">{t('lp.footer.tagline2', 'Building the future, one intelligent solution at a time.')}</p>
          </div>
          <div className="footer-section">
            <h4>{t('lp.footer.platform', 'Platform')}</h4>
            <Link to="/cpd-pro">{t('lp.footer.cpdpro', 'CPD Pro Platform')}</Link>
          </div>
          <div className="footer-section">
            <h4>{t('lp.footer.company', 'Company')}</h4>
            <a href="#story">{t('lp.nav.ourStory', 'Our Story')}</a>
            <a href="#mission">{t('lp.nav.mission', 'Our Mission')}</a>
            <a href="mailto:service@protagon.ai">{t('lp.footer.contactUs', 'Contact Us')}</a>
          </div>
          <div className="footer-section">
            <h4>{t('lp.footer.legal', 'Legal')}</h4>
            <a href="/privacy">{t('lp.footer.privacy', 'Privacy Policy')}</a>
            <a href="/terms">{t('lp.footer.terms', 'Terms of Service')}</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 Protagon. {t('lp.footer.copyright', 'Empowering humanity through AI innovation.')}</p>
        </div>
      </div>
    </footer>
  </>
)};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <Routes>
          <Route path="/" element={<MainLandingPage />} />
          <Route path="/cpd-pro" element={<CPDProLandingPage />}>
            <Route index element={<CpdProHomePage />} />
            <Route path="abonnementen" element={<CpdProAbonnementenPage />} />
            <Route path="waarom-cpd-pro" element={<CpdProWaaromPage />} />
            <Route path="accreditatie" element={<CpdProAccreditatiePage />} />
            <Route path="faq" element={<CpdProFaqPage />} />
          </Route>
          <Route path="/reset-password/:token" element={<PasswordResetPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
